'use strict';

const SELF_ORDERING_SELECTOR = 'input[type="checkbox"][name*="selfOrdering"]';
const FIELD_WRAPPER_SELECTOR = '.clearfix';
const ERROR_CLASS = 'error';
const HELP_INLINE_CLASS = 'help-inline';
const HELP_INLINE_SELECTOR = `.${HELP_INLINE_CLASS}[data-client-validation]`;

/** @type {Readonly<Record<string, { label: string, required?: boolean, email?: boolean }>>} */
const FIELD_RULES = {
    name: { label: 'Nom et prénom', required: true },
    email: { label: 'E-mail', required: true, email: true },
    phone: { label: 'Téléphone' },
    address: { label: 'Adresse', required: true },
    zipCode: { label: 'NPA', required: true },
    city: { label: 'Localité', required: true },
    country: { label: 'Pays', required: true },
    offer: { label: 'Offre', required: true },
    paymentMethod: { label: 'Mode de paiement', required: true },
    recipientName: { label: 'Nom et prénom du propriétaire', required: true },
    recipientAddress: { label: 'Adresse du propriétaire', required: true },
    recipientZipCode: { label: 'NPA du propriétaire', required: true },
    recipientCity: { label: 'Localité du propriétaire', required: true },
    recipientCountry: { label: 'Pays du propriétaire', required: true },
    remarks: { label: 'Remarques' },
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * @param {HTMLFormElement} form
 * @param {string} identifier
 * @returns {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null}
 */
function findField(form, identifier) {
    const field = form.querySelector(`[name*="[${identifier}]"]`);
    if (
        field instanceof HTMLInputElement ||
        field instanceof HTMLSelectElement ||
        field instanceof HTMLTextAreaElement
    ) {
        return field;
    }
    return null;
}

/**
 * @param {HTMLElement} field
 * @returns {Element | null}
 */
function findFieldWrapper(field) {
    return field.closest(FIELD_WRAPPER_SELECTOR);
}

/**
 * @param {string} label
 * @returns {string}
 */
function requiredMessage(label) {
    return `Le champ « ${label} » est obligatoire.`;
}

/**
 * @param {string} label
 * @returns {string}
 */
function emailMessage(label) {
    return `Veuillez saisir une adresse e-mail valide pour « ${label} ».`;
}

/**
 * @param {HTMLElement} field
 */
function clearFieldError(field) {
    const wrapper = findFieldWrapper(field);
    if (!wrapper) {
        return;
    }

    wrapper.classList.remove(ERROR_CLASS);
    field.removeAttribute('aria-invalid');
    wrapper.querySelectorAll(HELP_INLINE_SELECTOR).forEach((message) => {
        message.remove();
    });
}

/**
 * @param {HTMLElement} field
 * @param {string} message
 */
function setFieldError(field, message) {
    const wrapper = findFieldWrapper(field);
    if (!wrapper) {
        return;
    }

    clearFieldError(field);

    wrapper.classList.add(ERROR_CLASS);
    field.setAttribute('aria-invalid', 'true');

    const feedback = document.createElement('span');
    feedback.className = HELP_INLINE_CLASS;
    feedback.setAttribute('data-client-validation', '');
    feedback.setAttribute('role', 'alert');
    feedback.textContent = message;

    const container = wrapper.querySelector('.input, [class*="container"]') || wrapper;
    container.appendChild(feedback);
}

/**
 * @param {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} field
 * @returns {string}
 */
function getFieldValue(field) {
    if (field instanceof HTMLSelectElement) {
        return field.value.trim();
    }

    if (field instanceof HTMLTextAreaElement) {
        return field.value.trim();
    }

    if (field.type === 'checkbox') {
        return field.checked ? field.value : '';
    }

    return field.value.trim();
}

/**
 * @param {HTMLFormElement} form
 * @returns {boolean}
 */
function isSelfOrdering(form) {
    const checkbox = form.querySelector(SELF_ORDERING_SELECTOR);
    return checkbox instanceof HTMLInputElement && checkbox.checked;
}

/**
 * @param {string} identifier
 * @param {boolean} orderingForSelf
 * @returns {boolean}
 */
function shouldValidateField(identifier, orderingForSelf) {
    if (!FIELD_RULES[identifier]) {
        return false;
    }

    if (identifier.startsWith('recipient') && orderingForSelf) {
        return false;
    }

    return true;
}

/**
 * @param {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} field
 * @param {{ label: string, required?: boolean, email?: boolean }} rule
 * @returns {string | null}
 */
function validateField(field, rule) {
    const value = getFieldValue(field);

    if (rule.required && value === '') {
        return requiredMessage(rule.label);
    }

    if (rule.email && value !== '' && !EMAIL_PATTERN.test(value)) {
        return emailMessage(rule.label);
    }

    return null;
}

/**
 * @param {HTMLFormElement} form
 * @returns {HTMLElement | null}
 */
function validateForm(form) {
    const orderingForSelf = isSelfOrdering(form);
    /** @type {HTMLElement | null} */
    let firstInvalidField = null;

    Object.entries(FIELD_RULES).forEach(([identifier, rule]) => {
        const field = findField(form, identifier);
        if (!field) {
            return;
        }

        clearFieldError(field);

        if (!shouldValidateField(identifier, orderingForSelf)) {
            return;
        }

        const message = validateField(field, rule);
        if (!message) {
            return;
        }

        setFieldError(field, message);

        if (!firstInvalidField) {
            firstInvalidField = field;
        }
    });

    return firstInvalidField;
}

/**
 * Validation côté client du formulaire de commande de cep de vigne.
 *
 * @param {HTMLFormElement} form
 */
export function initVineStockOrderFormValidation(form) {
    form.addEventListener('submit', (event) => {
        const firstInvalidField = validateForm(form);
        if (!firstInvalidField) {
            return;
        }

        event.preventDefault();
        firstInvalidField.focus({ preventScroll: true });
        firstInvalidField.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });

    form.addEventListener('input', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) {
            return;
        }

        if (
            target instanceof HTMLInputElement ||
            target instanceof HTMLSelectElement ||
            target instanceof HTMLTextAreaElement
        ) {
            clearFieldError(target);
        }
    });

    form.addEventListener('change', (event) => {
        const target = event.target;
        if (target instanceof HTMLInputElement && target.matches(SELF_ORDERING_SELECTOR)) {
            Object.keys(FIELD_RULES)
                .filter((identifier) => identifier.startsWith('recipient'))
                .forEach((identifier) => {
                    const field = findField(form, identifier);
                    if (field) {
                        clearFieldError(field);
                    }
                });
            return;
        }

        if (
            target instanceof HTMLInputElement ||
            target instanceof HTMLSelectElement ||
            target instanceof HTMLTextAreaElement
        ) {
            clearFieldError(target);
        }
    });
}
